import { useCallback, useEffect, useRef, useState } from "react";
import {
	Box,
	CircularProgress,
	GlobalStyles,
	Paper,
	Typography,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import moment from "moment";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";

import { StraightenerRunsToolbar } from "./StraightenerRunsToolbar";
import { StraightenerRunsSummary } from "./StraightenerRunsSummary";
import {
	useStraightenerPickers,
	useStraightenerRuns,
} from "../hooks/useStraightenerRuns";
import type { StraightenerRunRow } from "../models/StraightenerRuns";

const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";

// The SP already returns Datetime converted to local time (same as legacy),
// so render the raw value without any timezone shift.
const fmtLocal = (v?: string | null): string =>
	v ? String(v).replace("T", " ").substring(0, 19) : "";

const fmtNum = (v: unknown, dec: number): string =>
	v == null || v === "" ? "" : Number(v).toFixed(dec);

// Decimal formatting matches the legacy Backgrid config: only Last Gap Time,
// Calibrated FPM and Material Thickness are fixed to 2 decimals. Set FPM, Width,
// Height and Profile Type are shown raw (no forced decimals). flex columns fill
// the grid width with no empty space on the right.
const COLUMN_DEFS: ColDef<StraightenerRunRow>[] = [
	{
		field: "Datetime",
		headerName: "Datetime",
		flex: 1.6,
		minWidth: 160,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => fmtLocal(p.data?.Datetime),
	},
	{
		field: "ProductSize",
		headerName: "Product Size",
		flex: 1.3,
		minWidth: 120,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "LastGapTime",
		headerName: "Last Gap Time",
		flex: 1.2,
		minWidth: 110,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => fmtNum(p.data?.LastGapTime, 2),
	},
	{
		field: "SetFPM",
		headerName: "Set FPM",
		flex: 1,
		minWidth: 90,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "CalibratedFPM",
		headerName: "Calibrated FPM",
		flex: 1.3,
		minWidth: 120,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => fmtNum(p.data?.CalibratedFPM, 2),
	},
	{
		field: "Width",
		headerName: "Width",
		flex: 0.9,
		minWidth: 80,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Height",
		headerName: "Height",
		flex: 0.9,
		minWidth: 80,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "MaterialThickness",
		headerName: "Material Thickness",
		flex: 1.4,
		minWidth: 130,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => fmtNum(p.data?.MaterialThickness, 2),
	},
	{
		field: "ProfileType",
		headerName: "Profile Type",
		flex: 1.1,
		minWidth: 100,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
];

const EXCEL_COLUMN_KEYS = [
	"Datetime",
	"ProductSize",
	"LastGapTime",
	"SetFPM",
	"CalibratedFPM",
	"Width",
	"Height",
	"MaterialThickness",
	"ProfileType",
];

const EXCEL_DECIMALS: Record<string, number> = {
	LastGapTime: 2,
	CalibratedFPM: 2,
	MaterialThickness: 2,
};

const URL_SCHEMA = {
	start: urlParam.date(() => moment().startOf("day").subtract(2, "days")),
	end: urlParam.date(() => moment().startOf("day")),
	productSizeId: urlParam.int(),
};

export const StraightenerRunsReport = () => {
	const [{ start, end, productSizeId }, setFilters, { dropValues }] =
		useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<StraightenerRunRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const dataQuery = useStraightenerRuns({
		start: startStr,
		end: endStr,
		productSizeId,
	});
	const pickersQuery = useStraightenerPickers();

	// A shared link can name a product size that is no longer offered.
	useEffect(() => {
		if (!pickersQuery.isSuccess || productSizeId == null) return;
		if (!pickersQuery.data.some((size) => size.Id === productSizeId))
			dropValues("productSizeId");
	}, [pickersQuery.isSuccess, pickersQuery.data, productSizeId, dropValues]);

	const rows = dataQuery.data ?? [];

	const handleGridReady = useCallback(
		(e: GridReadyEvent<StraightenerRunRow>) => {
			gridApiRef.current = e.api;
		},
		[],
	);

	const handleRefresh = useCallback(() => {
		dataQuery.refetch();
		pickersQuery.refetch();
	}, [dataQuery, pickersQuery]);

	const handleExportToExcel = useCallback(() => {
		gridApiRef.current?.exportDataAsExcel({
			fileName: "Straightener_Runs.xlsx",
			columnKeys: EXCEL_COLUMN_KEYS,
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				const data = params.node?.data as StraightenerRunRow | undefined;
				if (colId === "Datetime") return fmtLocal(data?.Datetime);
				if (colId in EXCEL_DECIMALS) {
					return fmtNum(
						data?.[colId as keyof StraightenerRunRow],
						EXCEL_DECIMALS[colId],
					);
				}
				return params.value ?? "";
			},
		});
	}, []);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}
	if (pickersQuery.error && !errorMsg) {
		setErrorMsg((pickersQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Straightener Runs
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<StraightenerRunsToolbar
					start={start}
					end={end}
					onStartChange={(d) => isUsableDate(d) && setFilters({ start: d })}
					onEndChange={(d) => isUsableDate(d) && setFilters({ end: d })}
					productSizeId={productSizeId}
					onProductSizeChange={(id) => setFilters({ productSizeId: id })}
					productSizes={pickersQuery.data ?? []}
					onRefresh={handleRefresh}
					onExportExcel={handleExportToExcel}
					isLoading={dataQuery.isFetching}
				/>
			</Paper>

			<StraightenerRunsSummary count={rows.length} />

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden" }}>
				{dataQuery.isLoading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<CircularProgress />
					</Box>
				) : (
					<AgGridReact<StraightenerRunRow>
						rowData={rows}
						columnDefs={COLUMN_DEFS}
						defaultColDef={{ resizable: true }}
						suppressContextMenu
						headerHeight={32}
						rowHeight={28}
						onGridReady={handleGridReady}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
